import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  styled,
  Typography,
  Grid,
  TextField,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Paper,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { MRT_ColumnDef } from "material-react-table";
import * as Yup from "yup";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import FormBuilderV4 from "../../../../shared/components/form-builder/form-builder-v4";
import { OptionType } from "../../../../shared/components/form-builder/form-config.model";
import CustomTable from "../../../../shared/components/table/custom-table";
import { cbLandingPageService } from "../tc-service/cb-landingpage.service";
import { CustomSelect } from "../../../../shared/components/inputfields/form-elements";
import { useFormActions } from "../../../../shared/hooks/form-builder.hooks";
import useRecordsStore, { Pagination } from "../../../../shared/hooks/useRecordStore";
import { getColumnsBySource } from "./cbLandingPageTable.config";
import { LoaderComponent } from "../../../../shared/components/loaders/page-loader";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";
import { TC_CREATE_ROUTE_CONFIG, TCAppConfig } from "../../../../shared/app-configs/tc-app-configs";
import { dtrackItService } from "../tc-service/dtrackit.service";
import { useApi } from "../../../../shared/hooks/useApi";
import CBReasonDetails from "./cb-reason-deatils";
import { ETCAppConfig } from "../../../../shared/app-configs/etc-app-configs";
import { Utills } from "../../../../shared/app-configs/utilit";
import { StringValidation } from "../../../../shared/utils/custom-validation.utils";
import useNotification from "../../../../shared/hooks/useNotification";
import FilterListIcon from '@mui/icons-material/FilterList';
import { mapTcNumberOptions, normalizeTcSearchValue } from "./tc-search.utils";

export const PageWrapper = styled("div")({
  padding: "0 25px 0px 25px",
  display: "grid",
  overflowY: "auto",
  flexDirection: "column",
  gap: "12px",
  flex: 1,
});

// Static TC Standards list
const TC_STANDARDS = [
  { value: 'CCS', label: 'CCS' },
  { value: 'GRS', label: 'GRS' },
  { value: 'OCS', label: 'OCS' },
  { value: 'RCS', label: 'RCS' },
  { value: 'RWS', label: 'RWS' },
  { value: 'RMS', label: 'RMS' },
  { value: 'RAS', label: 'RAS' },
  { value: 'RDS', label: 'RDS' },
  { value: 'MMS', label: 'MMS' }
];

const CBLandingPage = () => {
  const formRef = useRef<any>(null);
  const navigate = useNavigate();
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [localSource, setLocalSource] = useState<string>("dTrackit");
  const { resetAllDatas } = useFormActions("")
  const { setDelayReason, setTCIssueDate, setEditTCInfo, cbLicensingCode, setTCStandardGOTS, setNewTCRequested } = useTransactionCertificateStore();
  const [submissionStatusOptions, setSubmissionStatusOptions] = useState<{ value: string; label: string }[]>([]);
  const { notify } = useNotification();
  const isCbUser = cbLicensingCode.startsWith("CB");
  const sourceOptions: OptionType[] = [
    { label: "TC (dTrackit)", value: "dTrackit" },
    // { label: "TC (historical)", value: "historical" }  
  ];
  const getTCSubmissionStatus = async () => {
    try {
      // Hardcoded submission status values instead of API call
      const submissionStatuses = ["Created", "Draft", "Amending", "Approved", "Rejected", "Submitted"];
      const result = submissionStatuses.map((status: string) => ({ value: status, label: status }));

      setSubmissionStatusOptions(result);
    } catch (err) {
      notify("Error setting TC Submission Status", "error");
      Utills.cl(`Error in setting TC Submission Status ${err}`);
    }
  };


  const links: OptionType[] = [
    { label: "TP1", value: "item1" },
    { label: "TP2", value: "item2" },
  ];
  const { executeGet } = useApi();

  const [singleValue, setSingleValue] = React.useState<string | number>("");

  const {
    dTrackitData,
    dTrackitDataV4,
    historicalData,
    eTrackitData,
    fetchRecords,
    selectedSource,
    setSelectedSource,
    isLoading,
  } = useRecordsStore();
  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState<number>(0)
  const filteredData =
    selectedSource === 'dTrackit' ? dTrackitData :
      selectedSource === 'dTrackitV4' ? dTrackitDataV4 :
        selectedSource === 'historical' ? historicalData :
          eTrackitData;
  const columns = useMemo(() => getColumnsBySource(selectedSource),
    [selectedSource]);
  const [countries, setCountries] = useState<{ value: string; label: string }[]>([]);

  const [isResetEnabled, setResetEnable] = useState(false);

  type LandingSearchParams = Omit<

    Parameters<typeof fetchRecords>[0],
    "pagination" | "rowCount" | "setRowCount"
  >;
  const lastSearchParamsRef = useRef<LandingSearchParams | null>(null);

  const getCountry = async () => {
    try {
      // Import static country codes instead of making API call
      const countryCodesData = require("../../../../shared/data/country-codes.json");

      // Most frequently used Textile Exchange seller/buyer countries (based on data analysis)
      const priorityCountries = ['CN', 'US', 'IN', 'BD', 'TR', 'PK', 'VN', 'GB', 'DE', 'IT', 'ES', 'FR', 'JP', 'KR', 'TW', 'HK', 'TH', 'ID', 'MY', 'SG'];

      // Transform to show "Country Name (CODE)" in dropdown
      const allCountries = countryCodesData
        .map((country: any) => ({
          value: country.code,
          label: `${country.name} (${country.code})`,
          isPriority: priorityCountries.includes(country.code)
        }));

      // Sort: priority countries first (alphabetically), then rest (alphabetically)
      const transformedData = allCountries.sort((a: any, b: any) => {
        if (a.isPriority && !b.isPriority) return -1;
        if (!a.isPriority && b.isPriority) return 1;
        return a.label.localeCompare(b.label);
      });

      Utills.cl(`getCountry static data loaded: ${transformedData.length} countries`);
      setCountries(transformedData);
    } catch (err) {
      Utills.cl(`Error loading static country codes: ${err}`);
    }
  };


  const handleTCNOChange = (value: unknown) => {
    formRef.current?.setFieldValue("tcNo", normalizeTcSearchValue(value));
  };
  const handleTCNOInputChange = (value: string) => {
    formRef.current?.setFieldValue("tcNo", normalizeTcSearchValue(value));
  };
  const getTCNoforSearch = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ) => {
    try {
      const queryParams: Record<string, any> = {
        tcNo: type === "query" ? normalizeTcSearchValue(value) : "",
        offset,
        name: type === "name" ? value : "",
        tcCbLicensingCode: cbLicensingCode,
      };

      const response: any = await executeGet(TCAppConfig.GET_TC_NO_v4, queryParams);

      // Check if API failed
      if (response?.data?.Failed) {
        console.warn("API Error:", response.data.Failed);
        return [];
      }

      return mapTcNumberOptions(response?.data);
    } catch (e) {
      console.error(`Error in Fetching TCNo ${e}`);
      return [];
    }
  };

  const onNewRequestTCClick = () => {
    setNewTCRequested(true)
    navigate("/v4/tc/create-tc/transaction-certificate")
  }
  useEffect(() => {
    const loadData = async () => {
      await fetchRecords({
        ...(lastSearchParamsRef.current ?? {
          tcCbLicensingCode: cbLicensingCode,
          source: "dTrackitV4",
        }),
        pagination,
        setRowCount,
      });
    };
    loadData();
  }, [cbLicensingCode, fetchRecords, pagination.pageIndex, pagination.pageSize]);
  useEffect(() => {
    if (cbLicensingCode) {
      setEditTCInfo({});
      setDelayReason(false);
      setTCIssueDate("");
      resetAllDatas();
      getTCSubmissionStatus();
      setTCStandardGOTS(false);
    }
  }, [cbLicensingCode]);

  useEffect(() => {
    getCountry();
  }, []); // Load once on mount - static data doesn't change


  const handlePageChange = useCallback(
    (newPagination: Pagination) => {
      setPagination({
        ...pagination,
        ...newPagination,
      });
    },
    [pagination, setPagination]
  );

  const formConfig = {
    pageName: "cb-landing-page",
    initialValues: {
      buyersCountry: [], // For multi-select
      startDate: null,
      endDate: null,
      tcStandard: [],
      source: "",
      tcNo: "",
      submissionStatus: ""
    },
    validationSchema: Yup.object().shape({
      buyersCountry: Yup.array().nullable(),
      startDate: Yup.string().nullable(),
      endDate: Yup.string().nullable(),
      tcStandard: Yup.array().nullable(),
      source: Yup.string().nullable(),
      tcNo: Yup.string().nullable(),
      submissionStatus: Yup.string().nullable(),
    }),
    sections: [
      {
        fields: [
          {
            label: "Buyer's Country/Area",
            name: "buyersCountry",
            type: "select",
            gridSize: { xs: 12, sm: 6, md: 4, lg: 4 },
            required: false,
            props: {
              size: "small",
              multiple: true,
              enableSearch: true,
              options: countries,
              placeholder: "Select Buyer's Country",
            },
          },
          // Start Date Field
          {
            label: "Issue Start Date",
            name: "startDate",
            type: "date",
            gridSize: { xs: 12, sm: 6, md: 4, lg: 4 },
            required: false,
            props: {
              placeholder: "Select Start Date",
            },
          },
          // End Date Field
          {
            label: "Issue End Date",
            name: "endDate",
            type: "date",
            gridSize: { xs: 12, sm: 6, md: 4, lg: 4 },
            required: false,
            props: {
              placeholder: "Select End Date",
            },
          },
          {
            label: "TC Number",
            name: "tcNo",
            type: "search",
            gridSize: { xs: 12, sm: 6, md: 4, lg: 4 },
            required: false,
            props: {
              placeholder: "Search TC Number.",
              onChange: handleTCNOChange,
              allowCustomValue: true,
              onInputValueChange: handleTCNOInputChange,
              displayKey: "label",
              onSearch: (query: string, offset: number) => {
                const response = getTCNoforSearch(query, offset, "query");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "Submission Status",
            name: "submissionStatus",
            type: "select",
            gridSize: { xs: 12, sm: 6, md: 4, lg: 4 },
            required: false,
            props: {
              size: "small",
              multiple: false,
              options: submissionStatusOptions,
              placeholder: "Select Submission Status",
            },
          },
          {
            label: "TC Standard",
            name: "tcStandard",
            type: "select",
            gridSize: { xs: 12, sm: 6, md: 4, lg: 4 },
            required: false,
            props: {
              size: "small",
              multiple: true,
              options: TC_STANDARDS,
              placeholder: "Select TC Standard",
            },
          },
          // {
          //   label: "Source",
          //   name: "source",
          //   type: "select",
          //   gridSize: { xs: 12, sm: 6, md: 4, lg: 4 },
          //   required: false,
          //   props: {
          //     size: "small",
          //     multiple: false,
          //     options: sourceOptions,
          //     value: localSource,
          //     onChange: (event: any) => {
          //       setLocalSource(event.target.value);
          //       setSelectedSource(event.target.value);
          //       formRef.current?.setFieldValue('source', event.target.value, false);
          //       checkFormValues();
          //     }
          //   },
          // }

        ],
      },
    ],
    handleSubmit: async (values: any) => {
      await getCBLandingPageRecords(values, 1);
    },
  };

  const getCBLandingPageRecords = async (values: any, isReseted?: number) => {
    try {
      if (isReseted == 1) {
        const isEmpty = (val: any) => StringValidation.isNullOrEmpty(val);
        const allFieldsEmpty = [values.tcNo, values.submissionStatus, values.startDate, values.endDate].every(isEmpty) && (!values.buyersCountry?.length) && (!values.tcStandard?.length);

        if (allFieldsEmpty) {
          notify("Please fill at least one field", "error");
          return;
        }
      }

      const searchParams: LandingSearchParams = {
        tcCbLicensingCode: cbLicensingCode,
        startDate: values.startDate,
        endDate: values.endDate,
        tcNo: normalizeTcSearchValue(values.tcNo),
        submissionStatus: values.submissionStatus,
        buyersCountry: values.buyersCountry?.length > 0 ? JSON.stringify(values.buyersCountry) : undefined,
        tcStandard: values.tcStandard.length > 0 ? JSON.stringify(values.tcStandard) : undefined,
        source: "dTrackitV4",
      };
      lastSearchParamsRef.current = searchParams;

      const resetPagination = { pageIndex: 0, pageSize: pagination.pageSize };
      if (pagination.pageIndex === 0) {
        await fetchRecords({
          ...searchParams,
          pagination: resetPagination,
          setRowCount,
        });
      } else {
        // The pagination effect performs the filtered page-zero request.
        setPagination(resetPagination);
      }
      if (isReseted == 1) {
        setResetEnable(true)
      }
      else {
        setResetEnable(false)
      }
    } catch (error) {
      console.log("Error fetching data: ", error);
    }
  };

  const checkFormValues = () => {
    const currentValues = formRef.current?.values;
    if (currentValues) {
      const isAnyValueFilled = Object.values(currentValues).some(
        value => (Array.isArray(value) && value.length > 0) ||
          (value !== null && value !== "" && value !== undefined)
      );
      setIsSearchEnabled(isAnyValueFilled);
    }
  };

  const handleRowClick = (row: any) => {
    setNewTCRequested(false)
    // Utills.cl(row);
    // if (row.tcMetadataStatus === "Submitted") {
    //   navigate(`/v4/tc/submited-tc/base-details/${row.tcId}`);
    // } else {
    //   navigate(`v4/tc/tc-details/base-details/${row.tcId}`);
    // }
  };

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const handleReset = async () => {
    formRef.current.resetForm();
    lastSearchParamsRef.current = null;
    const resetPagination = { pageIndex: 0, pageSize: pagination.pageSize };
    const params = {
      tcCbLicensingCode: cbLicensingCode,
      startDate: null,
      endDate: null,
      tcNo: null,
      buyersCountry: [],
      tcStandard: [],
      source: selectedSource,
      submissionStatus: null,
      pagination: resetPagination
    }

    setResetEnable(false)
    await getCBLandingPageRecords(params, 0);
  }

  const handleSingleChange = (
    event: SelectChangeEvent<string | number | string[]>
  ) => {
    setSingleValue(event.target.value as string | number);
  };
  Utills.cl(columns, filteredData);

  return (
    <>
      <PageWrapper>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          mb="20px"
        >
          <Typography
            variant="h5"
            fontWeight="600"
            sx={{ color: '#2C4748', letterSpacing: '-0.5px' }}
          >
            Transaction Certificates - Version 4.0
          </Typography>
          {isCbUser && (
            <StyledButton
              onClick={onNewRequestTCClick}
              variant="contained"
              sx={{
                height: "40px",
                backgroundColor: '#F0CD7F',
                color: '#1C1C1C',
                '&:hover': {
                  backgroundColor: '#E0B86F',
                }
              }}
            >
              Create New Transaction Certificate
            </StyledButton>
          )}
        </Box>

        {/* Integrated Premium Filter Card */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid rgba(44, 71, 72, 0.12)',
            borderRadius: '12px',
            backgroundColor: '#FFFFFF',
            overflow: 'hidden',
            mb: 3,
          }}
        >
          {/* Inner padding for the filter fields */}
          <Box sx={{ p: 2.5, pb: 1.5 }}>
            <FormBuilderV4
              formConfig={formConfig}
              onSubmit={formConfig.handleSubmit}
              formRef={formRef}
            />
          </Box>

          {/* Styled footer action toolbar */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              px: 2.5,
              py: 1.5,
              backgroundColor: '#F8FAF9',
              borderTop: '1px solid rgba(44, 71, 72, 0.08)',
            }}
          >
            {/* Left side: column filter instruction tip */}
            <Box display="flex" alignItems="center" gap={1}>
              <FilterListIcon
                sx={{
                  color: '#2C4748',
                  fontSize: '18px',
                  opacity: 0.8,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: '#6A7879',
                  fontWeight: 500,
                }}
              >
                You can use column filters in the table below to filter loaded data on the current page.
              </Typography>
            </Box>

            {/* Right side: Search & Reset buttons */}
            <Box display="flex" alignItems="center" gap={1.5}>
              {isResetEnabled && (
                <StyledButton
                  onClick={handleReset}
                  variant="outlined"
                  sx={{
                    height: "36px",
                    backgroundColor: 'transparent',
                    border: '1px solid rgba(44, 71, 72, 0.3)',
                    color: '#2C4748',
                    marginRight: 0, // Override default margin
                    '&:hover': {
                      backgroundColor: 'rgba(44, 71, 72, 0.05)',
                      border: '1px solid #2C4748',
                    }
                  }}
                >
                  Reset
                </StyledButton>
              )}
              <StyledButton
                onClick={handleSubmit}
                variant="contained"
                sx={{
                  height: "36px",
                  backgroundColor: '#F0CD7F',
                  color: '#1C1C1C',
                  marginRight: 0, // Override default margin
                  '&:hover': {
                    backgroundColor: '#E0B86F',
                  }
                }}
              >
                Search
              </StyledButton>
            </Box>
          </Box>
        </Paper>

        <CustomTable
          key={selectedSource}
          columns={columns}
          columnVisibility={selectedSource === "dTrackitV4" ? { tcId: false } : {}}
          data={filteredData}
          title="TransactionData"
          enableTopToolbar={true}
          enableBottomToolbar={true}
          // enableColumnOrdering={false}
          paginations={pagination} // Pass current pagination state
          setPaginations={setPagination}
          rowCount={rowCount}
          isLoadingTodos={isLoading}
          onClickRow={handleRowClick}
          hiddenColumns={["tcParentMetadataStatus", "tcId"]}
        />
      </PageWrapper>

    </>
  );
};

export default CBLandingPage;
